import React from 'react';
import { DetailedWalletTransaction } from '../../../types';
import { useSettings } from '../../../lib/settings/settingsContext.tsx';

interface AdminUserTransactionsTableProps {
  transactions: DetailedWalletTransaction[];
}

const TRANSACTION_LABELS: Record<DetailedWalletTransaction['type'], string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  bet_placement: 'Trade Placed',
  bet_win: 'Trade Won',
  bet_loss: 'Trade Lost',
  bet_cancelled: 'Trade Cancelled',
  market_cancellation_refund: 'Market cancellation refund',
  service_fee: 'Service Fee',
  payout: 'Payout',
  platform_liquidity_return: 'Platform liquidity return',
  fee_refund: 'Fee Refund',
  affiliate_commission: 'Affiliate Commission',
  creator_commission: 'Creator Commission',
};

export function AdminUserTransactionsTable({ transactions }: AdminUserTransactionsTableProps) {
  const { formatCurrency } = useSettings();

  if (!transactions.length) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
        No transactions found for this user.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <HeaderCell>Date</HeaderCell>
              <HeaderCell>Type</HeaderCell>
              <HeaderCell>Description</HeaderCell>
              <HeaderCell className="text-right">Amount</HeaderCell>
              <HeaderCell>Status</HeaderCell>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {transactions.map((transaction) => {
              const amountClass = transaction.amount > 0 ? 'text-green-600' : transaction.amount < 0 ? 'text-red-600' : 'text-gray-700';
              return (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <BodyCell>
                    {new Date(transaction.created_at).toLocaleString()}
                  </BodyCell>
                  <BodyCell>
                    <span className="font-medium text-gray-900">
                      {TRANSACTION_LABELS[transaction.type] || transaction.type}
                    </span>
                  </BodyCell>
                  <BodyCell>
                    {renderDescription(transaction)}
                  </BodyCell>
                  <BodyCell align="right">
                    <span className={`font-medium ${amountClass}`}>
                      {formatCurrency(transaction.amount)}
                    </span>
                  </BodyCell>
                  <BodyCell>
                    <StatusBadge status={transaction.status} />
                  </BodyCell>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function renderDescription(transaction: DetailedWalletTransaction) {
  if (transaction.marketTitle && transaction.outcomeName) {
    return (
      <div>
        <div className="text-gray-900">{transaction.marketTitle}</div>
        <div className="text-xs text-gray-500">Outcome: {transaction.outcomeName}</div>
      </div>
    );
  }

  if (transaction.description) {
    return <span className="text-gray-600">{transaction.description}</span>;
  }

  return <span className="text-gray-400">—</span>;
}

interface HeaderCellProps {
  children: React.ReactNode;
  className?: string;
}

function HeaderCell({ children, className }: HeaderCellProps) {
  return (
    <th
      scope="col"
      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 ${className || ''}`}
    >
      {children}
    </th>
  );
}

interface BodyCellProps {
  children: React.ReactNode;
  align?: 'left' | 'right';
}

function BodyCell({ children, align = 'left' }: BodyCellProps) {
  return (
    <td className={`px-4 py-3 whitespace-nowrap text-sm text-gray-700 ${align === 'right' ? 'text-right' : ''}`}>
      {children}
    </td>
  );
}

interface StatusBadgeProps {
  status: DetailedWalletTransaction['status'];
}

function StatusBadge({ status }: StatusBadgeProps) {
  const color = getStatusClasses(status);
  const label = status.charAt(0).toUpperCase() + status.slice(1);

  return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${color}`}>{label}</span>;
}

function getStatusClasses(status: DetailedWalletTransaction['status']) {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-700';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    case 'failed':
    default:
      return 'bg-red-100 text-red-700';
  }
}

